import Image from 'next/image';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import { generateArticleSchema } from '../../utils/schemaGenerators';


export const metadata = {
  title: 'Top 10 Trends in Kitchen Remodeling',
  image: '/blog/kitchen-remodeling-trends.webp',
  date: 'May 05, 2025',
  author: 'Modern Build Team',
  excerpt: 'Discover the top 10 kitchen remodeling trends of 2025—smart layouts, bold finishes, and high-tech upgrades to elevate the heart of your home.',
  category: 'Kitchen Remodeling',
  tags: ['Kitchen Remodeling', 'General Home Improvement', 'Remodeling & Renovation', 'Home Renovation'],
};

export default function Top10KitchenTrends() {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="title" content={metadata.title} />
        <meta name="description" content={metadata.excerpt} />
        <meta name="author" content={metadata.author} />
        <meta name="keywords" content={metadata.tags.join(', ')} />
        <link rel="canonical" href={`https://modernbuildremodel.com/blog/top-10-kitchen-trends`} />

        <meta property="og:title" content={metadata.title} />
        <meta property="og:description" content={metadata.excerpt} />
        <meta property="og:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta property="og:url" content={`https://modernbuildremodel.com/blog/top-10-kitchen-trends`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.excerpt} />
        <meta name="twitter:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta name="twitter:site" content="@modernbuildhome" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": `https://modernbuildremodel.com/blog`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Top 10 Kitchen Remodeling Trends",
                  "item": `https://modernbuildremodel.com/blog/top-10-kitchen-trends`
                },
              ]
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateArticleSchema(metadata, 'top-10-kitchen-trends')),
          }}
        />
      </Head>

      <Header />

      <div className="bg-white">
        <Hero category="top10KitchenTrends" page="page" hasContentH1={false}/>

        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <p className="text-sm text-black mb-2">
            <span className="capitalize text-primary tracking-[3px]">By {metadata.author} / {metadata.date}</span>
          </p>

          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] mb-10 rounded-lg overflow-hidden">
            <Image
              src={metadata.image}
              alt={`Modern Build Home Remodeling post: ${metadata.title}`}
              title={metadata.title}
              fill
              sizes="(max-width: 768px) 100vw, 700px"
              className="object-cover rounded-lg"
            />
          </div>

<div className="max-w-none text-md text-black leading-relaxed space-y-8">
  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 shadow-sm">
    <p className="text-xl font-semibold mb-2">
      2025 Kitchen Remodeling Trends: Function Meets Personality
    </p>
    <p>
      In recent years, the <strong>kitchen has evolved</strong> from a purely functional zone into the 
      true <em>beating heart of the home</em>. Gone are the days of sterile minimalism—this year’s designs 
      lean into warmth, character, and comfort.
    </p>
  </div>

  <p>
    Whether you're an avid cook, a weekend host, or just someone who enjoys 
    peaceful mornings over a cup of coffee, a thoughtful kitchen remodel can 
    redefine your daily routine. It's not just about the look—<strong>it’s about the lifestyle it supports.</strong>
  </p>

  <div className="bg-white border-l-4 border-primary pl-4 py-2">
    <h3 className="text-lg font-bold text-primary">🏠 What Homeowners Are Prioritizing</h3>
    <p className="mt-2">
      This year, the focus is on comfort, efficiency, and versatility:
    </p>
    <ul className="list-disc pl-6 mt-2 space-y-1">
      <li><strong>Ergonomic layouts</strong> that improve movement and flow</li>
      <li><strong>Energy-efficient appliances</strong> to save money and reduce impact</li>
      <li><strong>Mixed materials</strong> like stone, wood, and brushed metal for texture</li>
      <li><strong>Sculptural lighting</strong> to define zones and add drama</li>
    </ul>
  </div>

  <h3 className="text-lg font-semibold mt-10">🧑‍🎨 Design That Tells a Story</h3>
  <p>
    Homeowners are moving beyond cookie-cutter layouts. Today’s remodels include:
  </p>
  <ul className="list-disc pl-6 space-y-1">
    <li>Custom vent hoods and artisanal tiles</li>
    <li>Bold cabinet colors like forest green or matte black</li>
    <li>Statement islands that serve as both prep zones and hangout spots</li>
  </ul>

  <p>
    These touches not only look good—they <em>express personality</em>. And let’s not forget the 
    rising role of <strong>sustainability</strong>: many homeowners are choosing recycled materials, 
    low-VOC paints, and layouts that reduce energy usage.
  </p>

  <div className="bg-primary text-white p-6 rounded-lg shadow mt-12">
    <h3 className="text-lg font-bold mb-2">✨ Key Takeaways for 2025</h3>
    <ul className="list-disc pl-6 text-white space-y-1">
      <li>Open layouts with multifunctional islands remain essential</li>
      <li>Mixed textures (wood, stone, and metal) = visual richness</li>
      <li>Hidden storage is a must for clean aesthetics</li>
      <li>Smart tech isn’t a gimmick—it’s practical and elegant</li>
      <li>Warm neutrals and deep colors are trending again</li>
    </ul>
  </div>

  <p className="mt-8">
    If you're dreaming of a <strong>kitchen that’s both innovative and personal</strong>, our team at 
    Modern Build is here to help—from 3D design and permit approvals to expert 
    craftsmanship that brings it all to life.
  </p>
</div>

        </div>

        <SocialProof />
        <ContactUs />
        <Footer />
      </div>
    </>
  );
}
